import { useState } from 'react';
import { NavLink as RouterNavLink, Outlet, useLocation, Link as RouterLink } from 'react-router-dom';

import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import Logo from '../../components/Logo';
import Iconify from '../../components/Iconify';
import useSettings from '../../hooks/useSettings';
import { DOCS, PATH_AUTH, PATH_DASHBOARD } from '../../routes/paths';

// ---------------------------------------------------------------------
//  Public docs layout — no auth required.
//
//  Top app bar: BetaData logo + product · search-style brand pill + Sign in /
//  Go to dashboard button + theme toggle.
//
//  Left rail: grouped section nav. Collapses to a drawer on mobile.
// ---------------------------------------------------------------------

const NAV = [
  {
    title: 'Getting started',
    items: [
      { label: 'Overview',         icon: 'eva:book-open-fill', to: DOCS.root },
      { label: 'Webhook',          icon: 'eva:link-2-fill',    to: DOCS.webhook },
    ],
  },
  {
    title: 'Telecom',
    items: [
      { label: 'Buy data',         icon: 'eva:wifi-fill',          to: DOCS.buydata },
      { label: 'Buy airtime',      icon: 'eva:phone-fill',         to: DOCS.buyairtime },
      { label: 'Airtime to cash',  icon: 'eva:swap-fill',          to: DOCS.cash },
      { label: 'Bulk SMS',         icon: 'eva:message-circle-fill',to: DOCS.bulksms },
    ],
  },
  {
    title: 'Utilities',
    items: [
      { label: 'Cable TV',         icon: 'eva:tv-fill',          to: DOCS.buycable },
      { label: 'Electricity',      icon: 'eva:flash-fill',       to: DOCS.buybill },
      { label: 'Education + JAMB', icon: 'mdi:school',           to: DOCS.buyexam },
    ],
  },
  {
    title: 'International & stores',
    items: [
      { label: 'eSIM',                  icon: 'eva:globe-fill',       to: DOCS.esim },
      { label: 'Smile · Alpha · Kirani',icon: 'eva:phone-call-fill',  to: DOCS.sak },
    ],
  },
  {
    title: 'Print cards',
    items: [
      { label: 'Data card',        icon: 'eva:credit-card-fill', to: DOCS.data_card },
      { label: 'Recharge card',    icon: 'eva:hash-fill',        to: DOCS.recharge_card },
    ],
  },
  {
    title: 'Verifications',
    items: [
      { label: 'Cable ID (IUC)',   icon: 'eva:tv-outline',       to: DOCS.verifyiuc },
      { label: 'Meter number',     icon: 'eva:flash-outline',    to: DOCS.verifymetter },
    ],
  },
];

const DRAWER_WIDTH = 264;

export default function PublicDocsLayout() {
  const theme = useTheme();
  const { themeMode, onChangeMode } = useSettings();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [open, setOpen] = useState(false);
  const isAuthed = !!window.localStorage.getItem('accessToken');

  const sideNav = <SideNav onClose={() => setOpen(false)} />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, lg: 64 } }}>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setOpen(true)} sx={{ mr: 1 }}>
              <Iconify icon="eva:menu-fill" width={22} />
            </IconButton>
          )}

          <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo disabledLink sx={{ width: 30, height: 30 }} />
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography sx={{ fontWeight: 800, letterSpacing: '-0.5px', fontSize: '1rem' }}>
                <Box component="span" sx={{ color: 'primary.main' }}>Beta</Box>
                <Box component="span" sx={{ color: 'secondary.main' }}>Data</Box>
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  borderLeft: (t) => `1px solid ${t.palette.divider}`,
                  pl: 0.75,
                }}
              >
                Docs
              </Typography>
            </Stack>
          </RouterLink>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              size="small"
              onClick={() => onChangeMode?.({ target: { value: themeMode === 'light' ? 'dark' : 'light' } })}
              title={themeMode === 'light' ? 'Switch to dark' : 'Switch to light'}
            >
              <Iconify icon={themeMode === 'light' ? 'eva:moon-fill' : 'eva:sun-fill'} width={18} />
            </IconButton>

            {isAuthed ? (
              <Button
                component={RouterLink}
                to={PATH_DASHBOARD.general.app}
                size="small"
                variant="contained"
                endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
                sx={{ fontWeight: 700, borderRadius: 1.5, ml: 0.5 }}
              >
                Dashboard
              </Button>
            ) : (
              <Button
                component={RouterLink}
                to={PATH_AUTH.login}
                size="small"
                variant="contained"
                endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
                sx={{ fontWeight: 700, borderRadius: 1.5, ml: 0.5 }}
              >
                Sign in
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      {isDesktop ? (
        <Box
          component="aside"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            position: 'fixed',
            top: 64,
            bottom: 0,
            left: 0,
            overflowY: 'auto',
            borderRight: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: 'background.paper',
          }}
        >
          {sideNav}
        </Box>
      ) : (
        <Drawer open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
          {sideNav}
        </Drawer>
      )}

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 9, lg: 11 },
          pb: 8,
          pl: { lg: `${DRAWER_WIDTH + 16}px` },
          pr: { lg: 2 },
          px: { xs: 2 },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------

function SideNav({ onClose }) {
  const { pathname } = useLocation();
  return (
    <Box sx={{ p: 2.5 }}>
      {NAV.map((group) => (
        <Box key={group.title} sx={{ mb: 2.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.68rem',
            }}
          >
            {group.title}
          </Typography>
          <Stack spacing={0.25}>
            {group.items.map((item) => {
              const active = pathname === item.to;
              return (
                <Box
                  key={item.to}
                  component={RouterNavLink}
                  to={item.to}
                  onClick={onClose}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 1.5,
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: active ? 'primary.main' : 'text.primary',
                    bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.text.primary, 0.04) },
                  }}
                >
                  <Iconify icon={item.icon} width={18} sx={{ color: active ? 'primary.main' : 'text.disabled' }} />
                  <span>{item.label}</span>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}
